/**
 * U.S. state comprehensive consumer privacy laws in effect, used by the
 * Privacy Policy's state-rights section. One entry per state so the table and
 * the per-state notes render from a single source.
 */
export interface StatePrivacyLaw {
  state: string
  law: string
  effective: string
  /** Days to respond to a verified request, before any permitted extension. */
  responseDays: number
  /** Whether the state grants a right to appeal a denied request. */
  appeal: boolean
  /** State-specific rights or differences beyond the common set. */
  notes: string
  /** Where to escalate if an appeal is denied. */
  regulator: string
}

export const statePrivacyLaws: StatePrivacyLaw[] = [
  {
    state: 'California',
    law: 'California Consumer Privacy Act, as amended by the California Privacy Rights Act (CCPA/CPRA)',
    effective: 'January 1, 2020 (amended January 1, 2023)',
    responseDays: 45,
    appeal: false,
    notes:
      'Right to opt out of "sharing" for cross-context behavioral advertising, right to limit use of sensitive personal information, and "Shine the Light" disclosures. See the California section below.',
    regulator: 'California Privacy Protection Agency (cppa.ca.gov)',
  },
  {
    state: 'Colorado',
    law: 'Colorado Privacy Act (CPA)',
    effective: 'July 1, 2023',
    responseDays: 45,
    appeal: true,
    notes: 'Universal opt-out signals (such as Global Privacy Control) must be honored.',
    regulator: 'Colorado Attorney General (coag.gov)',
  },
  {
    state: 'Connecticut',
    law: 'Connecticut Data Privacy Act (CTDPA)',
    effective: 'July 1, 2023',
    responseDays: 45,
    appeal: true,
    notes: 'Universal opt-out signals must be honored.',
    regulator: 'Connecticut Attorney General (portal.ct.gov/ag)',
  },
  {
    state: 'Delaware',
    law: 'Delaware Personal Data Privacy Act (DPDPA)',
    effective: 'January 1, 2025',
    responseDays: 45,
    appeal: true,
    notes:
      'Right to obtain a list of the categories of third parties to which we have disclosed your personal data. Universal opt-out signals must be honored.',
    regulator: 'Delaware Department of Justice (attorneygeneral.delaware.gov)',
  },
  {
    state: 'Indiana',
    law: 'Indiana Consumer Data Protection Act (ICDPA)',
    effective: 'January 1, 2026',
    responseDays: 45,
    appeal: true,
    notes: 'Right to correct applies to inaccuracies in data you previously provided.',
    regulator: 'Indiana Attorney General (in.gov/attorneygeneral)',
  },
  {
    state: 'Iowa',
    law: 'Iowa Consumer Data Protection Act (ICDPA)',
    effective: 'January 1, 2025',
    responseDays: 90,
    appeal: true,
    notes:
      'Rights to access, delete, portability, and opt out of sale. Iowa law does not include a right to correct or to opt out of profiling.',
    regulator: 'Iowa Attorney General (iowaattorneygeneral.gov)',
  },
  {
    state: 'Kentucky',
    law: 'Kentucky Consumer Data Protection Act (KCDPA)',
    effective: 'January 1, 2026',
    responseDays: 45,
    appeal: true,
    notes: '—',
    regulator: 'Kentucky Attorney General (ag.ky.gov)',
  },
  {
    state: 'Maryland',
    law: 'Maryland Online Data Privacy Act (MODPA)',
    effective: 'October 1, 2025',
    responseDays: 45,
    appeal: true,
    notes:
      'Right to obtain a list of the categories of third parties to which we have disclosed your personal data. Universal opt-out signals must be honored.',
    regulator: 'Maryland Attorney General (marylandattorneygeneral.gov)',
  },
  {
    state: 'Minnesota',
    law: 'Minnesota Consumer Data Privacy Act (MCDPA)',
    effective: 'July 31, 2025',
    responseDays: 45,
    appeal: true,
    notes:
      'Right to obtain a list of the specific third parties to which we have disclosed your personal data, and the right to question the result of a profiling decision and learn the reasons behind it. Universal opt-out signals must be honored.',
    regulator: 'Minnesota Attorney General (ag.state.mn.us)',
  },
  {
    state: 'Montana',
    law: 'Montana Consumer Data Privacy Act (MCDPA)',
    effective: 'October 1, 2024',
    responseDays: 45,
    appeal: true,
    notes: 'Universal opt-out signals must be honored.',
    regulator: 'Montana Department of Justice (dojmt.gov)',
  },
  {
    state: 'Nebraska',
    law: 'Nebraska Data Privacy Act (NDPA)',
    effective: 'January 1, 2025',
    responseDays: 45,
    appeal: true,
    notes: 'Universal opt-out signals must be honored.',
    regulator: 'Nebraska Attorney General (ago.nebraska.gov)',
  },
  {
    state: 'New Hampshire',
    law: 'New Hampshire Privacy Act (SB 255)',
    effective: 'January 1, 2025',
    responseDays: 45,
    appeal: true,
    notes: 'Universal opt-out signals must be honored.',
    regulator: 'New Hampshire Attorney General (doj.nh.gov)',
  },
  {
    state: 'New Jersey',
    law: 'New Jersey Data Privacy Act (NJDPA)',
    effective: 'January 15, 2025',
    responseDays: 45,
    appeal: true,
    notes: 'Universal opt-out signals must be honored.',
    regulator: 'New Jersey Division of Consumer Affairs (njconsumeraffairs.gov)',
  },
  {
    state: 'Oregon',
    law: 'Oregon Consumer Privacy Act (OCPA)',
    effective: 'July 1, 2024',
    responseDays: 45,
    appeal: true,
    notes:
      'Right to obtain a list of the specific third parties to which we have disclosed your personal data. Universal opt-out signals must be honored.',
    regulator: 'Oregon Department of Justice (doj.state.or.us)',
  },
  {
    state: 'Rhode Island',
    law: 'Rhode Island Data Transparency and Privacy Protection Act',
    effective: 'January 1, 2026',
    responseDays: 45,
    appeal: true,
    notes:
      'Right to know the third parties to which we have sold or may sell your personal data.',
    regulator: 'Rhode Island Attorney General (riag.ri.gov)',
  },
  {
    state: 'Tennessee',
    law: 'Tennessee Information Protection Act (TIPA)',
    effective: 'July 1, 2025',
    responseDays: 45,
    appeal: true,
    notes: '—',
    regulator: 'Tennessee Attorney General (tn.gov/attorneygeneral)',
  },
  {
    state: 'Texas',
    law: 'Texas Data Privacy and Security Act (TDPSA)',
    effective: 'July 1, 2024',
    responseDays: 45,
    appeal: true,
    notes: 'Universal opt-out signals must be honored.',
    regulator: 'Texas Attorney General (texasattorneygeneral.gov)',
  },
  {
    state: 'Utah',
    law: 'Utah Consumer Privacy Act (UCPA)',
    effective: 'December 31, 2023',
    responseDays: 45,
    appeal: false,
    notes:
      'Rights to access, delete, portability, and opt out of sale and targeted advertising. Utah law does not include a right to correct, opt out of profiling, or appeal.',
    regulator: 'Utah Division of Consumer Protection (dcp.utah.gov)',
  },
  {
    state: 'Virginia',
    law: 'Virginia Consumer Data Protection Act (VCDPA)',
    effective: 'January 1, 2023',
    responseDays: 45,
    appeal: true,
    notes: '—',
    regulator: 'Virginia Attorney General (oag.state.va.us)',
  },
]
